import React, { useState } from 'react';
import { CloseIcon, StarIcon, CheckCircleIcon, CancelIcon, DeleteICon } from '../../../public/icons';

const ReviewDetailsModal = ({ isOpen, onClose, review, onModerate, onDelete }) => {
  const [moderationNote, setModerationNote] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!isOpen || !review) return null;

  const handleModerate = async (status) => {
    setProcessing(true);
    try {
      await onModerate(review._id, status, moderationNote);
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this review permanently? This cannot be undone.')) return;
    setProcessing(true);
    try {
      await onDelete(review._id);
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    spam: 'bg-gray-100 text-gray-700'
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[100]" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[45%] bg-[#F9F6F2] z-[999] overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b mx-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Review Details</h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Review the submission and approve, reject, or delete.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB]"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Product */}
            <div className="flex gap-4">
              <img
                src={review.productSnapshot?.image || '/placeholder.jpg'}
                alt={review.productSnapshot?.title}
                className="w-24 h-28 object-cover border border-[#DBDBDB]"
              />
              <div className="text-left">
                <h3 className="font-medium text-[#1C2A46]">
                  {review.productSnapshot?.title || 'Unknown Product'}
                </h3>
                {review.productSnapshot?.category && (
                  <p className="text-xs text-[#6B635D] mt-1">
                    {review.productSnapshot.category}
                  </p>
                )}
                {review.productSnapshot?.price > 0 && (
                  <p className="text-sm text-[#1C2A46] mt-1 font-medium">
                    ₦{review.productSnapshot.price.toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Review content */}
            <div className="bg-white border border-[#DBDBDB] p-4 text-left">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`w-4 h-4 ${
                        star <= review.rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="text-sm text-[#6B635D] ml-1">{review.rating}/5</span>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[review.status]}`}>
                  {review.status}
                </span>
              </div>

              {review.title && (
                <h4 className="font-medium text-[#1C2A46] text-base mb-2">
                  {review.title}
                </h4>
              )}

              <p className="text-sm text-[#6B635D] whitespace-pre-wrap">
                {review.text}
              </p>
            </div>

            {/* Reviewer info */}
            <div className="bg-white border border-[#DBDBDB] p-4 text-left space-y-2">
              <h4 className="font-medium text-[#1C2A46] text-sm mb-2">Reviewer</h4>
              <p className="text-sm text-[#1C2A46]">
                <span className="text-[#6B635D]">Name: </span>
                {review.reviewerName || review.user?.firstName + ' ' + review.user?.lastName}
              </p>
              <p className="text-sm text-[#1C2A46] break-all">
                <span className="text-[#6B635D]">Email: </span>
                {review.reviewerEmail || review.user?.email}
              </p>
              <p className="text-sm text-[#1C2A46]">
                <span className="text-[#6B635D]">Submitted: </span>
                {new Date(review.createdAt).toLocaleString()}
              </p>
              {review.isVerifiedPurchase && (
                <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Verified Purchase
                </span>
              )}
            </div>

            {/* Media */}
            {review.media && review.media.length > 0 && (
              <div className="text-left">
                <h4 className="font-medium text-[#1C2A46] text-sm mb-2">Attached Media</h4>
                <div className="grid grid-cols-3 gap-2">
                  {review.media.map((m, i) => (
                    <div key={i} className="aspect-square border border-[#DBDBDB] overflow-hidden">
                      {m.type === 'video' ? (
                        <video src={m.url} controls className="w-full h-full object-cover" />
                      ) : (
                        <img src={m.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            
          </div>

          {/* Footer actions */}
          <div className="p-6 border-t border-[#DBDBDB] space-y-2">
            {review.status !== 'approved' && (
              <button
                onClick={() => handleModerate('approved')}
                disabled={processing}
                className="w-full py-3 bg-[#2F8F5B] text-white font-medium hover:bg-[#267548] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CheckCircleIcon size={16} color="#fff" />
                {processing ? 'Processing…' : 'Approve Review'}
              </button>
            )}

            {review.status !== 'rejected' && (
              <button
                onClick={() => handleModerate('rejected')}
                disabled={processing}
                className="w-full py-3 bg-[#A82121] text-white font-medium hover:bg-[#8a1b1b] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CancelIcon size={16} color="#fff" />
                {processing ? 'Processing…' : 'Reject Review'}
              </button>
            )}

            <button
              onClick={handleDelete}
              disabled={processing}
              className="w-full py-3 border border-[#A82121] text-[#A82121] font-medium hover:bg-[#A82121] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <DeleteICon size={16} />
              {processing ? 'Processing…' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewDetailsModal;